<template>
  <!-- FI-2215 the body of the "event added" toast. the add-entry dialog closes
       on save and hands this to $root's toaster. the text is the open button,
       so its name is what it says and the toast's status region reads the
       number once; the button's pseudo stretches over the whole body, and the
       x dismisses it early -->
  <div :class="['od-added-toast-row', { 'od-added-toast-row--still': still }]">
    <span class="od-added-toast-check" aria-hidden="true">
      <span class="od-added-toast-ring"></span>
      <svg viewBox="0 0 76 76" focusable="false">
        <circle class="od-added-toast-disc" cx="38" cy="38" r="38"/>
        <path class="od-added-toast-mark" d="M22 39 L33 50 L55 27"/>
      </svg>
    </span>
    <!-- the build drops whitespace between tags, so the space that keeps the
         two lines two words apart in the button's name is an expression. the
         flex column does not render it -->
    <button type="button" class="od-added-toast-open" :aria-describedby="hintId" @click="$emit('open')">
      <span class="od-added-toast-title"><u>Event #{{ eventId }}</u> added</span>{{ ' ' }}<span v-if="detail" class="od-added-toast-sub">{{ detail }}</span>
    </button>
    <span :id="hintId" hidden>Opens the event</span>
    <button type="button" class="close od-added-toast-close" aria-label="Close" @click="$emit('close')">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
</template>

<script>
// a paused toast resumes with at least this long, as bootstrap-vue's does
const MIN_RESUME_MS = 1000

export default {
  name: 'od_added_toast',
  props: {
    eventId: { type: [Number, String], required: true },
    // the category's display name, plus ", charter <ref>" when there is one
    detail: { type: String, default: '' },
    // reduced motion: the finished check, no animation
    still: { type: Boolean, default: false },
    // emits close after this long; 0 never does. the toast owns the timer
    // (bootstrap-vue's pauses on hover only) so keyboard focus holds it too
    autoHideMs: { type: Number, default: 0 },
  },
  computed: {
    hintId() {
      return 'od-added-toast-hint-' + this._uid
    },
  },
  mounted() {
    this._remaining = this.autoHideMs
    this._holds = { hover: false, focus: false }
    // bootstrap-vue's .toast (focusable itself) is the whole target, frame
    // included; mounted on its own, as in a spec, it is the row
    const host = (this.$el.closest && this.$el.closest('.toast')) || this.$el
    this._host = host
    this._listeners = {
      mouseenter: () => this.hold('hover', true),
      mouseleave: () => this.hold('hover', false),
      focusin: () => this.hold('focus', true),
      focusout: evt => { if (!host.contains(evt.relatedTarget)) this.hold('focus', false) },
    }
    Object.keys(this._listeners).forEach(k => host.addEventListener(k, this._listeners[k]))
    this.runTimer()
  },
  beforeDestroy() {
    this.stopTimer()
    Object.keys(this._listeners || {}).forEach(k => this._host.removeEventListener(k, this._listeners[k]))
  },
  methods: {
    // hover or focus anywhere on the toast holds it; it resumes once neither does
    hold(kind, on) {
      this._holds[kind] = on
      if (on) this.stopTimer()
      else this.runTimer()
    },
    runTimer() {
      if (!this.autoHideMs || this._timer || this._holds.hover || this._holds.focus) return
      this._started = Date.now()
      this._timer = setTimeout(() => {
        this._timer = null
        this.$emit('close')
      }, this._remaining)
    },
    stopTimer() {
      if (!this._timer) return
      clearTimeout(this._timer)
      this._timer = null
      this._remaining = Math.max(this._remaining - (Date.now() - this._started), MIN_RESUME_MS)
    },
  },
}
</script>

<style scoped>
/* the lively check from the confirm-check preview, at toast scale: the disc
   pops with a small overshoot, the tick draws, one soft ring pulses out, and
   the two lines rise in. plays once, css only */
.od-added-toast-row {
  /* isolate: the open button's stretched pseudo sits at z -1 inside this
     row, under the text but over nothing outside the toast */
  position: relative; isolation: isolate;
  display: flex; align-items: center; gap: 12px;
  /* the right side leaves room for the x */
  padding: 12px 40px 12px 14px;
  color: var(--fi-text, #1d1d1f);
}
/* clicks on the check fall through to the open button's pseudo */
.od-added-toast-check { position: relative; flex: 0 0 auto; width: 40px; height: 40px; pointer-events: none; }
.od-added-toast-check svg { position: relative; display: block; width: 100%; height: 100%; overflow: visible; }
/* midnight.css lifts the disc and ring greens */
.od-added-toast-disc {
  fill: #16a34a;
  transform-origin: 38px 38px; transform-box: view-box;
  animation: od-added-pop 420ms cubic-bezier(.2, .9, .3, 1.35) both;
}
.od-added-toast-mark {
  fill: none; stroke: #fff; stroke-width: 6; stroke-linecap: round; stroke-linejoin: round;
  stroke-dasharray: 48; stroke-dashoffset: 0;
  animation: od-added-draw 360ms ease-out 260ms both;
}
.od-added-toast-ring {
  position: absolute; top: 0; right: 0; bottom: 0; left: 0;
  border-radius: 50%; background: rgba(22, 163, 74, 0.16); opacity: 0;
  animation: od-added-ring 720ms ease-out 200ms both;
}
@keyframes od-added-pop {
  0% { transform: scale(.3); opacity: 0; }
  60% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes od-added-draw {
  0% { stroke-dashoffset: 48; }
  100% { stroke-dashoffset: 0; }
}
@keyframes od-added-ring {
  0% { transform: scale(.6); opacity: .9; }
  100% { transform: scale(1.55); opacity: 0; }
}
@keyframes od-added-rise {
  0% { transform: translateY(6px); opacity: 0; }
  100% { transform: none; opacity: 1; }
}

/* the text is the open button. its pseudo stretches over the whole body
   (under the text; the check lets clicks through), so the toast is one
   target, and it carries the hover wash and the focus ring */
.od-added-toast-open {
  display: flex; flex-direction: column; flex: 1 1 auto; min-width: 0;
  margin: 0; padding: 0; border: 0; background: transparent;
  font: inherit; color: inherit; text-align: left; line-height: 1.35; cursor: pointer;
}
.od-added-toast-open::before {
  content: ''; position: absolute; top: 0; right: 0; bottom: 0; left: 0; z-index: -1;
}
.od-added-toast-open:focus { outline: none; }
.od-added-toast-open:focus-visible::before { outline: 2px solid var(--fi-ops-accent, #2e6da4); outline-offset: -2px; }
/* keyed on the button, so the x's own hover never reads as "open" */
.od-added-toast-open:hover::before { background: var(--fi-chrome-hover-bg-strong, rgba(21, 50, 77, 0.08)); }
.od-added-toast-open:hover .od-added-toast-title { text-decoration: underline; text-underline-offset: 3px; }

.od-added-toast-title {
  font-size: 14.5px; font-weight: 600; color: var(--fi-text, #1d1d1f);
  animation: od-added-rise 360ms ease-out 380ms both;
}
.od-added-toast-title u { text-underline-offset: 3px; }
.od-added-toast-sub {
  margin-top: 2px; font-size: 12.5px; color: var(--fi-text-muted, #6b7280); overflow-wrap: anywhere;
  animation: od-added-rise 360ms ease-out 460ms both;
}

/* above the stretched pseudo so it stays its own target */
.od-added-toast-close {
  position: absolute; top: 4px; right: 4px; z-index: 2;
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  font-size: 20px; font-weight: 400; line-height: 1;
  color: var(--fi-text-muted, #6b7280); text-shadow: none; opacity: .85;
}
/* bootstrap's .close:not(:disabled):not(.disabled):hover/:focus sets .75;
   this outranks it so hover and focus brighten instead of dimming */
.close.od-added-toast-close:not(:disabled):hover,
.close.od-added-toast-close:not(:disabled):focus { color: var(--fi-text, #1d1d1f); opacity: 1; }
.od-added-toast-close:focus { outline: none; }
.od-added-toast-close:focus-visible { outline: 2px solid var(--fi-ops-accent, #2e6da4); outline-offset: -2px; }

/* reduced motion: the finished check and the text in place. the class is set
   from matchMedia when the toast is made; the media query covers a change
   after that */
.od-added-toast-row--still .od-added-toast-disc,
.od-added-toast-row--still .od-added-toast-mark,
.od-added-toast-row--still .od-added-toast-ring,
.od-added-toast-row--still .od-added-toast-title,
.od-added-toast-row--still .od-added-toast-sub { animation: none; }
@media (prefers-reduced-motion: reduce) {
  .od-added-toast-disc, .od-added-toast-mark, .od-added-toast-ring,
  .od-added-toast-title, .od-added-toast-sub { animation: none; }
}

@media (max-width: 600px) {
  /* a thumb-sized x, glyph and target */
  .od-added-toast-row { padding-right: 52px; }
  .od-added-toast-close { top: 0; right: 0; width: 48px; height: 48px; font-size: 28px; }
}
</style>

<!-- unscoped: bootstrap-vue draws the toast wrapper in its toaster on <body>,
     outside this component's scoped DOM. tokens carry the theme, so midnight
     gets a dark surface with no rule of its own -->
<style>
.b-toaster .b-toast .toast.od-added-toast {
  background-color: var(--fi-surface, #fff);
  border-color: var(--fi-line-strong, rgba(0, 0, 0, 0.1));
  color: var(--fi-text, #1d1d1f);
}
.toast-body.od-added-toast-body { padding: 0; }

/* its own toaster, bottom center: up top it covered the page's Add entry,
   Export and Settings buttons, and before that the top bar. it layers
   under the top bar (1030), so a bar menu or the phone account sheet opens
   over it. bootstrap-vue's placement and stacking rules key on its named
   toasters, so this one carries both */
.b-toaster.od-added-toaster {
  position: fixed; bottom: 0; left: .5rem; right: .5rem; z-index: 1029;
  height: 0; margin: 0; padding: 0; overflow: visible;
}
/* 24px up, level with the fleet assistant button (fleet_assistant_launcher.vue) */
.b-toaster.od-added-toaster .b-toaster-slot {
  position: absolute; bottom: 24px; left: 0; right: 0;
  width: 100%; max-width: 350px; margin: 0 auto; padding: 0;
}
/* narrower, the centered toast reaches that button's column (48px tall,
   12 to 24px in from the corner, wider with its label), so it rides above */
@media (max-width: 820px) {
  .b-toaster.od-added-toaster .b-toaster-slot { bottom: 80px; }
}
.b-toaster.od-added-toaster .b-toast.b-toaster-enter-active,
.b-toaster.od-added-toaster .b-toast.b-toaster-leave-active,
.b-toaster.od-added-toaster .b-toast.b-toaster-move { transition: transform .175s; }
.b-toaster.od-added-toaster .b-toast.b-toaster-enter-to .toast.fade,
.b-toaster.od-added-toaster .b-toast.b-toaster-enter-active .toast.fade { transition-delay: .175s; }
.b-toaster.od-added-toaster .b-toast.b-toaster-leave-active { position: absolute; transition-delay: .175s; }
.b-toaster.od-added-toaster .b-toast.b-toaster-leave-active .toast.fade { transition-delay: 0s; }
</style>
